import {
    Box,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';

import { RequestStatus } from 'app/components/request-status';
import { Translate } from 'app/components/translate';
import { UserPicker } from 'app/components/user-picker';
import { SeamRequest } from 'app/model/seam-request';
import { Formatters } from 'app/utils/formatters';
import { Strings } from 'app/utils/strings';
import { FScoreStoreDialog } from 'features/qa/f-score/f-score-store-dialog';

interface SeamSummaryProps {
    request: SeamRequest;
    onChange: (field: keyof SeamRequest, value: unknown) => void;
}

const renderPublishDates = (request: SeamRequest): Record<string, string> => {
    const dates = request.publishDates;

    const rows: Record<string, string> = {};

    if (dates.preProd) {
        rows['Preprod Deploy'] = Formatters.dateTime(dates.preProd);
    }

    if (dates.prod) {
        rows['Prod Deploy'] = Formatters.dateTime(dates.prod);
    }

    return rows;
};

const FSCORE_PRECISION = 4;

const formatFScore = (score?: number) => {
    if (undefined === score) {
        return '[none]';
    }

    return score.toFixed(FSCORE_PRECISION);
};

export const SeamSummary: React.FunctionComponent<SeamSummaryProps> = (props) => {
    const { request, onChange } = props;
    const [fscoreStore, setFscoreStore] = useState<number>();

    const dates = renderPublishDates(request);

    return (
        <Box
            component={Paper}
            className='p-2 ml-2'
            sx={{
                'bgcolor': 'warning.light',
                '& .MuiTableCell-root': {
                    borderBottomColor: 'warning.main',
                },
            }}
        >
            <Typography variant='h5'>SEAM Request</Typography>
            <RequestStatus status={request.currentPlace} />
            <Table size='small'>
                <TableBody>
                    <TableRow>
                        <TableCell>Type:</TableCell>
                        <TableCell>
                            <Translate options={{ ns: 'headings' }} tkey={Strings.seamRequestType(request)} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <Typography variant='body2' noWrap>
                                F-Score
                                <br />
                                <Link component='button' onClick={() => setFscoreStore(request.store.id)}>
                                    More
                                </Link>
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant='body2' noWrap>
                                before: {formatFScore(request.fScoreStat.scoreBefore)}
                                <br />
                                after: {formatFScore(request.fScoreStat.scoreAfter)}
                            </Typography>
                        </TableCell>
                    </TableRow>
                    {Object.keys(dates).map((key) => (
                        <TableRow id={key}>
                            <TableCell>{key}:</TableCell>
                            <TableCell>{dates[key]}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TextField
                fullWidth
                name='dueDate'
                label='Due Date'
                variant='outlined'
                size='small'
                type='date'
                className='mt-2'
                value={request.dueDate}
                onChange={(evt) => {
                    onChange('dueDate', evt.target.value);
                }}
                InputLabelProps={{
                    shrink: true,
                }}
            />
            <UserPicker
                className='mt-2'
                onChange={(evt, user) => {
                    if (user) {
                        onChange('manager', user);
                    }
                }}
                value={request.manager}
                label='Manager'
                size='small'
            />
            <UserPicker
                className='mt-2'
                onChange={(evt, user) => {
                    if (user) {
                        onChange('tuner', user);
                    }
                }}
                value={request.tuner}
                label='Pattern Creator'
                size='small'
            />
            <UserPicker
                className='mt-2'
                onChange={(evt, user) => {
                    if (user) {
                        onChange('owner', user);
                    }
                }}
                value={request.owner}
                label='Assignee'
                size='small'
            />
            <TableContainer>
                <Table size='small'>
                    <TableBody>
                        {request.labelTasks.map((task) => (
                            <TableRow key={task.id}>
                                <TableCell>[status]</TableCell>
                                <TableCell>{Formatters.userDisplayName(task.owner)}</TableCell>
                                <TableCell>
                                    {task.completed}/{task.total}
                                </TableCell>
                                <TableCell>[graph]</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <FScoreStoreDialog storeId={fscoreStore} onClose={() => setFscoreStore(undefined)} />
        </Box>
    );
};
